import { Component } from '@theme/component';
import { debounce, onDocumentLoaded } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/** Hover intent: brief pointer passes cancel before this elapses. */
const ACTIVATE_DELAY = 280;
/** Shorter delay when moving to another top-level item (previous panel is cleared first). */
const SWITCH_ACTIVATE_DELAY = 120;
/** Slightly below ACTIVATE_DELAY so close does not race ahead of open when scrubbing items. */
const DEACTIVATE_DELAY = 320;

/**
 * Viewport X of the left/right edges of the padding box of `element` (where `left` on `position:absolute`
 * children is measured from the left padding edge — not `getBoundingClientRect().left`, which is the border box).
 * @param {HTMLElement} element
 */
function getAbsoluteContainingBlockHorizontalEdges(element) {
  const r = element.getBoundingClientRect();
  const cs = getComputedStyle(element);
  const bl = parseFloat(cs.borderLeftWidth) || 0;
  const br = parseFloat(cs.borderRightWidth) || 0;
  return {
    left: r.left + bl,
    right: r.right - br,
  };
}

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  #abortController = new AbortController();

  /** @type {AbortController | undefined} */
  #submenuLayoutAbort;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #activateTimerId;

  /** @type {HTMLElement | null | undefined} */
  #columnsCache;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerleave', this.#onLeaveEntireMenu);

    onDocumentLoaded(this.#preloadImages);
    onDocumentLoaded(this.#initSubmenuLayoutListeners);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('pointerleave', this.#onLeaveEntireMenu);
    this.#submenuLayoutAbort?.abort();
    this.#submenuLayoutAbort = undefined;
    this.#columnsCache = undefined;
    this.#clearActivateTimer();
    this.#abortController.abort();
  }

  /** @returns {HTMLElement | null} */
  #getColumns = () => {
    if (this.#columnsCache !== undefined) return this.#columnsCache;
    const el = this.closest('.header__columns');
    this.#columnsCache = el instanceof HTMLElement ? el : null;
    return this.#columnsCache;
  };

  #clearActivateTimer = () => {
    if (this.#activateTimerId !== undefined) {
      clearTimeout(this.#activateTimerId);
      this.#activateTimerId = undefined;
    }
  };

  /**
   * @param {PointerEvent | FocusEvent} event
   * @param {number} delayMs
   */
  #scheduleActivate = (event, delayMs) => {
    this.#clearActivateTimer();
    if (delayMs <= 0) {
      this.#activateHandler(event);
      return;
    }
    this.#activateTimerId = setTimeout(() => {
      this.#activateTimerId = undefined;
      this.#activateHandler(event);
    }, delayMs);
  };

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Time to allow for a closing animation between initiating a deactivation and actually deactivating the active item.
   * @returns {number}
   */
  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  /**
   * Schedule activating the selected menu item after {@link ACTIVATE_DELAY} (hover intent).
   * When moving to another top-level item: the previous item is closed immediately (no stale
   * `aria-expanded` / panel), then the new item opens after {@link SWITCH_ACTIVATE_DELAY}.
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.#debouncedDeactivate.cancel();
    this.#clearActivateTimer();

    if (!(event.target instanceof Element)) {
      this.#scheduleActivate(event, ACTIVATE_DELAY);
      return;
    }

    const item = findMenuItem(event.target);
    const switching = item != null && this.#state.activeItem != null && item !== this.#state.activeItem;

    if (switching) {
      this.#instantCloseActiveItem();
      this.#scheduleActivate(event, SWITCH_ACTIVATE_DELAY);
    } else {
      this.#scheduleActivate(event, ACTIVATE_DELAY);
    }
  };

  /**
   * After delayed activation, resolve the element under the pointer (stale `event.target` is unreliable).
   * @param {PointerEvent | FocusEvent} event
   * @returns {Element | null}
   */
  #resolveActivationOrigin = (event) => {
    if (event instanceof PointerEvent) {
      const { clientX, clientY } = event;
      if (Number.isFinite(clientX) && Number.isFinite(clientY)) {
        const hit = document.elementFromPoint(clientX, clientY);
        if (!hit || !this.contains(hit)) return null;
        return hit;
      }
      if (!this.matches(':hover')) return null;
      return event.target instanceof Element ? event.target : null;
    }
    return event.target instanceof Element ? event.target : null;
  };

  /**
   * Activate the selected menu item with a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #activateHandler = (event) => {
    this.#debouncedDeactivate.cancel();

    const origin = this.#resolveActivationOrigin(event);
    if (!origin) return;

    this.dispatchEvent(new MegaMenuHoverEvent());

    this.removeAttribute('data-animating');

    let item = findMenuItem(origin);

    if (!item || item == this.#state.activeItem) return;

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
      previouslyActiveItem.removeAttribute('data-animating');
      const prevSubmenu = findSubmenu(previouslyActiveItem);
      if (prevSubmenu) {
        delete prevSubmenu.dataset.active;
      }
    }

    this.#state.activeItem = item;

    const submenu = findSubmenu(item);

    if (submenu) {
      submenu.dataset.active = '';
    }

    /* Measure and set --dropdown-submenu-left before aria-expanded so the first paint is not at the CSS 50% fallback. */
    this.#syncSubmenuLayout();

    item.ariaExpanded = 'true';
    this.ariaExpanded = 'true';

    const submenuHeight = submenu ? submenu.offsetHeight : 0;

    this.style.setProperty('--submenu-height', `${submenuHeight}px`);
    this.style.setProperty('--submenu-opacity', '1');
  };

  /**
   * Close the current item immediately (no exit animation). Used before opening another item.
   */
  #instantCloseActiveItem = () => {
    const item = this.#state.activeItem;
    if (!item) return;

    this.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.style.removeProperty('--megamenu-caret-left');
    this.style.removeProperty('--dropdown-submenu-left');

    const submenu = findSubmenu(item);

    item.ariaExpanded = 'false';
    item.removeAttribute('data-animating');
    if (submenu) {
      delete submenu.dataset.active;
    }

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    this.removeAttribute('data-animating');
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    this.#clearActivateTimer();

    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);

    // Make sure the item to be deactivated is still the active one. Ideally
    // we cancelled the debounce before the item was changed, but just in case.
    if (item === this.#state.activeItem) {
      if (DEACTIVATE_DELAY <= 0) {
        this.#debouncedDeactivate.cancel();
        this.#deactivate();
      } else {
        this.#debouncedDeactivate();
      }
    }
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;

    this.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.style.removeProperty('--megamenu-caret-left');
    this.style.removeProperty('--dropdown-submenu-left');

    const submenu = findSubmenu(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    item.removeAttribute('data-animating');
    if (submenu) {
      delete submenu.dataset.active;
    }
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #debouncedDeactivate = debounce(this.#deactivate, DEACTIVATE_DELAY);

  /**
   * When the pointer leaves the whole header menu (not just moving between items), close.
   * Delegated `on:pointerleave` on each top-level link can miss edge cases; this matches leave semantics.
   *
   * Flyouts are positioned against `.header__columns` while this element only wraps the nav strip.
   * Moving from a top-level link into the panel crosses padding/gap that is still inside the columns
   * but outside `header-menu`, so `relatedTarget` is not contained here — without deferring, we would
   * close before the pointer reaches the submenu.
   * @param {MouseEvent | PointerEvent} event
   */
  #onLeaveEntireMenu = (event) => {
    const next = event.relatedTarget;
    if (next instanceof Node && this.contains(next)) return;

    this.#clearActivateTimer();
    this.#debouncedDeactivate.cancel();

    if (!this.#state.activeItem) return;

    const columns = this.#getColumns();
    const stillInHeaderRow = next instanceof Node && columns instanceof Element && columns.contains(next);

    if (stillInHeaderRow) {
      if (DEACTIVATE_DELAY <= 0) {
        this.#debouncedDeactivate.cancel();
        this.#deactivate();
      } else {
        this.#debouncedDeactivate();
      }
    } else {
      this.#deactivate();
    }
  };

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  /**
   * Positions dropdown/compact mega under the active link: one `--dropdown-submenu-left` write
   * (clamped) per sync to avoid horizontal jump from ideal-then-clamp or a second rAF pass.
   */
  #syncSubmenuLayout = () => {
    const active = this.#state.activeItem;
    if (!(active instanceof HTMLElement)) {
      this.style.removeProperty('--dropdown-submenu-left');
      this.style.removeProperty('--megamenu-caret-left');
      return;
    }

    const menuHasAnchoredDropdown =
      this.querySelector('.menu-list__list-item--dropdown-submenu') ||
      this.querySelector('.menu-list__submenu--megamenu-compact');

    if (!menuHasAnchoredDropdown) {
      this.style.removeProperty('--dropdown-submenu-left');
    }

    const li = active.closest('.menu-list__list-item');
    const isDropdownSubmenu = li?.classList.contains('menu-list__list-item--dropdown-submenu');
    const isCompactMega = li?.querySelector('.menu-list__submenu--megamenu-compact');
    const needsAnchor = Boolean(isDropdownSubmenu || isCompactMega);

    const columns = this.#getColumns();

    if (menuHasAnchoredDropdown && needsAnchor && columns) {
      const linkRect = active.getBoundingClientRect();
      const colEdges = getAbsoluteContainingBlockHorizontalEdges(columns);
      const centerX = linkRect.left + linkRect.width / 2;
      const idealLeft = Math.round(centerX - colEdges.left);

      const submenuEl = active.closest('.menu-list__list-item')?.querySelector(
        '.menu-list__submenu--dropdown, .menu-list__submenu--megamenu-compact',
      );
      if (submenuEl instanceof HTMLElement) {
        void submenuEl.offsetWidth;
      }

      /* Single write: avoid painting ideal then clamped (horizontal jump). */
      this.#applyClampedDropdownLeft(idealLeft, active);

      this.#syncMegamenuCaret();
      return;
    }

    if (needsAnchor && !columns) {
      this.style.removeProperty('--dropdown-submenu-left');
    } else if (!needsAnchor) {
      this.style.removeProperty('--dropdown-submenu-left');
    }

    this.#syncMegamenuCaret();

    requestAnimationFrame(() => {
      if (this.#state.activeItem !== active) return;
      this.#syncMegamenuCaret();
    });
  };

  /**
   * Triangle under the active top-level link (same idea as dropdown inner ::before).
   * Mega panel position is pure CSS (absolute vs .header__columns); only the caret needs measuring.
   */
  #syncMegamenuCaret = () => {
    if (!this.querySelector('.menu-list__submenu--megamenu')) {
      this.style.removeProperty('--megamenu-caret-left');
      return;
    }

    const active = this.#state.activeItem;
    if (!(active instanceof HTMLElement)) {
      this.style.removeProperty('--megamenu-caret-left');
      return;
    }

    const li = active.closest('.menu-list__list-item');
    const submenu = li?.querySelector('.menu-list__submenu--megamenu');
    const inner = submenu?.querySelector('.menu-list__submenu-inner--megamenu');
    if (!(inner instanceof HTMLElement)) {
      this.style.removeProperty('--megamenu-caret-left');
      return;
    }

    const linkRect = active.getBoundingClientRect();
    const innerRect = inner.getBoundingClientRect();
    const centerX = linkRect.left + linkRect.width / 2;
    let left = Math.round(centerX - innerRect.left);
    const min = 12;
    const max = Math.max(min, Math.round(innerRect.width - 12));
    left = Math.min(max, Math.max(min, left));

    this.style.setProperty('--megamenu-caret-left', `${left}px`);
  };

  /**
   * Set `--dropdown-submenu-left` once per pass: clamp to viewport when panel width is known;
   * otherwise fall back to ideal (avoids stale value + rAF-only correction jump).
   * @param {number} idealLeft
   * @param {HTMLElement} active
   */
  #applyClampedDropdownLeft = (idealLeft, active) => {
    const columns = this.#getColumns();
    if (!(columns instanceof HTMLElement)) {
      this.style.setProperty('--dropdown-submenu-left', `${idealLeft}px`);
      return;
    }

    const colEdges = getAbsoluteContainingBlockHorizontalEdges(columns);
    const submenu = active.closest('.menu-list__list-item')?.querySelector(
      '.menu-list__submenu--dropdown, .menu-list__submenu--megamenu-compact',
    );
    if (!(submenu instanceof HTMLElement)) {
      this.style.setProperty('--dropdown-submenu-left', `${idealLeft}px`);
      return;
    }

    const width = submenu.getBoundingClientRect().width;
    if (!(width > 0)) {
      this.style.setProperty('--dropdown-submenu-left', `${idealLeft}px`);
      return;
    }

    const pad = 8;
    const half = width / 2;
    const idealCenterVp = colEdges.left + idealLeft;

    const minCenterVp = Math.max(colEdges.left + pad + half, pad + half);
    const maxCenterVp = Math.min(colEdges.right - pad - half, window.innerWidth - pad - half);

    let clampedCenterVp;
    if (minCenterVp <= maxCenterVp) {
      clampedCenterVp = Math.min(maxCenterVp, Math.max(minCenterVp, idealCenterVp));
    } else {
      clampedCenterVp = (minCenterVp + maxCenterVp) / 2;
    }

    const clampedLeft = Math.round(clampedCenterVp - colEdges.left);
    this.style.setProperty('--dropdown-submenu-left', `${clampedLeft}px`);
  };

  #initSubmenuLayoutListeners = () => {
    const needsLayout =
      this.querySelector('.menu-list__submenu--megamenu') ||
      this.querySelector('.menu-list__list-item--dropdown-submenu');
    if (!needsLayout) return;

    this.#submenuLayoutAbort?.abort();
    this.#submenuLayoutAbort = new AbortController();
    const { signal } = this.#submenuLayoutAbort;
    window.addEventListener(
      'resize',
      () => {
        this.#syncSubmenuLayout();
      },
      { signal, passive: true }
    );
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;
  return element?.querySelector('[ref="menuitem"]') ?? element.closest?.('.menu-list__list-item')?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
